// Sprint C frontend: AI quiz/notes dialogs, Forum, Challenges, QR attendance, Drop-off analytics.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkles, FileText, Wand2, Trophy, Flame, MessageCircle, Plus, QrCode, ScanLine, Loader2, CheckCircle2, Copy, Award } from "lucide-react";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF0066]/40";

// ============ AI QUIZ GENERATOR ============
export function AIQuizGeneratorButton() {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [content, setContent] = useState("");
  const [num, setNum] = useState(5);
  const [difficulty, setDifficulty] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const gen = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/ai/generate-quiz", { topic, content, num_questions: num, difficulty });
      setResult(data);
      toast.success(`Generated ${data.count} questions`);
    } catch (er) { toast.error(er.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(result?.questions, null, 2));
    toast.success("Copied — paste into Quiz form");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-ai-quiz"><Wand2 className="w-4 h-4" /> AI Quiz</button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wand2 className="w-5 h-5 text-fuchsia-300" /> AI Quiz Generator</DialogTitle>
          <div className="text-xs text-white/50">Claude Sonnet 4.5 se MCQs generate karo — topic ya notes paste karo.</div>
        </DialogHeader>
        <form onSubmit={gen} className="space-y-3">
          <label className="block"><div className="text-xs text-white/50 mb-1">Topic</div><input value={topic} onChange={(e) => setTopic(e.target.value)} className={inp} placeholder="e.g. SEO ranking factors" data-testid="ai-quiz-topic" /></label>
          <label className="block"><div className="text-xs text-white/50 mb-1">Source notes (optional — paste lecture transcript or notes)</div><textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)} className={inp} /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><div className="text-xs text-white/50 mb-1">Number of questions (3-20)</div><input type="number" min="3" max="20" value={num} onChange={(e) => setNum(Number(e.target.value))} className={inp} /></label>
            <label className="block"><div className="text-xs text-white/50 mb-1">Difficulty</div>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inp}><option>easy</option><option>medium</option><option>hard</option></select>
            </label>
          </div>
          <button disabled={busy} className="w-full bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full py-2.5 text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="ai-quiz-generate">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating with Claude…</> : <><Sparkles className="w-4 h-4" /> Generate</>}
          </button>
        </form>
        {result && (
          <div className="mt-4 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/[0.04] p-3 space-y-3" data-testid="ai-quiz-result">
            <div className="flex justify-between items-center">
              <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold">Generated {result.count} questions</div>
              <button onClick={copyJson} className="btn-ghost text-xs"><Copy className="w-3 h-3" /> Copy JSON</button>
            </div>
            {result.questions.map((q, i) => (
              <div key={q.id} className="rounded border border-white/10 p-2 space-y-1">
                <div className="text-sm font-medium">{i + 1}. {q.question}</div>
                <ul className="text-xs space-y-0.5">
                  {q.options.map((o, j) => <li key={j} className={j === q.correct_index ? "text-emerald-300 font-semibold" : "text-white/70"}>{j === q.correct_index ? "✓" : "•"} {o}</li>)}
                </ul>
                <div className="text-[10px] text-white/40 italic">💡 {q.explanation}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ AI NOTES SUMMARIZER ============
export function AISummarizeWidget() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [style, setStyle] = useState("bullets");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const go = async (e) => {
    e.preventDefault();
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/ai/summarize-notes", { text, style });
      setOut(data);
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-ai-summary"><FileText className="w-4 h-4" /> AI Summary</button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileText className="w-5 h-5 text-fuchsia-300" /> AI Notes Summarizer</DialogTitle>
          <div className="text-xs text-white/50">Lambe notes ko bullets / mind-map / TL;DR me convert karo.</div>
        </DialogHeader>
        <form onSubmit={go} className="space-y-3">
          <label className="block"><div className="text-xs text-white/50 mb-1">Paste your notes / transcript</div>
            <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} className={inp} required minLength={50} data-testid="ai-summary-text" />
          </label>
          <label className="block"><div className="text-xs text-white/50 mb-1">Style</div>
            <select value={style} onChange={(e) => setStyle(e.target.value)} className={inp}><option value="bullets">Bullet points</option><option value="mindmap">Mind-map (hierarchical)</option><option value="tldr">TL;DR (short paragraph)</option></select>
          </label>
          <button disabled={busy || text.length < 50} className="w-full bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full py-2.5 text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="ai-summary-go">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Summarizing…</> : <><Sparkles className="w-4 h-4" /> Summarize</>}
          </button>
        </form>
        {out && (
          <div className="mt-4 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/[0.04] p-3 space-y-3" data-testid="ai-summary-result">
            <div>
              <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-1">Summary</div>
              <p className="text-sm text-white/80 whitespace-pre-line">{out.summary}</p>
            </div>
            {out.key_points?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-amber-300 font-bold mb-1">Key points</div>
                <ul className="text-sm text-white/80 list-disc list-inside space-y-0.5">{out.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul>
              </div>
            )}
            {out.action_items?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-1">Action items</div>
                <ul className="text-sm text-white/80 list-disc list-inside space-y-0.5">{out.action_items.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ COMMUNITY FORUM ============
export function CommunityForumTab() {
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const { data: list } = await api.get(`/forum/posts${activeTopic ? "?topic=" + activeTopic : ""}`);
      setPosts(list);
      const { data: ts } = await api.get("/forum/topics");
      setTopics(ts);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTopic]);

  return (
    <div className="space-y-4" data-testid="forum-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setActiveTopic(null)} className={`px-3 py-1 rounded-full text-xs ${!activeTopic ? "bg-[#FF0066] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>All</button>
          {topics.map((t) => (
            <button key={t.topic} onClick={() => setActiveTopic(t.topic)} className={`px-3 py-1 rounded-full text-xs ${activeTopic === t.topic ? "bg-[#FF0066] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>
              #{t.topic} <span className="opacity-60">({t.count})</span>
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-4 py-2 text-xs inline-flex items-center gap-1" data-testid="open-forum-create">
          <Plus className="w-3.5 h-3.5" /> New post
        </button>
      </div>
      {posts.length === 0 && <div className="text-center text-white/40 py-10 text-sm">No discussions yet{activeTopic ? ` in #${activeTopic}` : ""}. Start one!</div>}
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className={`rounded-lg border ${p.pinned ? "border-amber-400/30" : "border-white/10"} bg-white/[0.02] p-3 hover:bg-white/[0.04]`} data-testid={`forum-post-${p.id}`}>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FF0066]/20 flex items-center justify-center text-[10px] font-bold shrink-0">{p.user_name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold">{p.user_name}</span>
                  <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-white/5 text-white/60">{p.user_role}</span>
                  {p.pinned && <span className="text-[9px] text-amber-300">📌 Pinned</span>}
                  <span className="text-[10px] text-white/40 ml-auto">{new Date(p.created_at).toLocaleString()}</span>
                </div>
                <div className="font-bold text-sm mt-1">{p.title}</div>
                <div className="text-xs text-white/70 mt-1 whitespace-pre-line line-clamp-3">{p.body}</div>
                <div className="mt-1.5 text-[10px] text-white/40 inline-flex items-center gap-3">
                  <span>#{p.topic}</span>
                  <span>▲ {p.upvote_count || 0}</span>
                  <span>💬 {p.reply_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ForumCreateDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} />
    </div>
  );
}

function ForumCreateDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", body: "", topic: "general" });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/forum/posts", f); toast.success("Posted"); onSaved(); onClose(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
        <DialogHeader><DialogTitle>New forum post</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><div className="text-xs text-white/50 mb-1">Title</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="forum-title" /></label>
          <label className="block"><div className="text-xs text-white/50 mb-1">Body</div><textarea rows={5} required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={inp} data-testid="forum-body" /></label>
          <label className="block"><div className="text-xs text-white/50 mb-1">Topic (one word)</div><input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value.replace(/\s+/g, "-").toLowerCase() })} className={inp} placeholder="seo / ads / career" /></label>
          <button disabled={busy} className="w-full bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full py-2 text-sm disabled:opacity-60" data-testid="forum-submit">{busy ? "Posting…" : "Post"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ DAILY CHALLENGES ============
export function ChallengesTab() {
  const [list, setList] = useState([]);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/challenges").then(({ data }) => setList(data)).catch(() => {});
    api.get("/challenges/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);
  const submit = async (cid) => {
    const text = window.prompt("Apna submission text/URL daalo:");
    if (!text) return;
    try { await api.post(`/challenges/${cid}/submit`, { submission_text: text }); toast.success("Submitted! Admin se approval ke baad points milenge."); api.get("/challenges").then(({ data }) => setList(data)); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="challenges-tab">
      <div className="lg:col-span-2 space-y-2">
        <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold flex items-center gap-2"><Flame className="w-3.5 h-3.5" /> Active challenges ({list.length})</div>
        {list.length === 0 && <div className="text-center text-white/40 py-8 text-sm">No active challenges right now.</div>}
        {list.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`challenge-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-amber-300 font-bold">{c.category}</div>
                <div className="font-bold mt-0.5">{c.title}</div>
                <div className="text-xs text-white/70 mt-1 whitespace-pre-line">{c.description}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xl font-bold text-[#FF0066]">+{c.points}</div>
                <div className="text-[10px] text-white/40">points</div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="text-[10px] text-white/40">Ends {new Date(c.active_until).toLocaleString()}</div>
              {c.submitted ? <span className="ml-auto text-[10px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Submitted</span> :
                <button onClick={() => submit(c.id)} className="ml-auto bg-[#FF0066] hover:bg-[#e6005c] text-white text-xs font-semibold rounded-full px-3 py-1" data-testid={`submit-challenge-${c.id}`}>Submit</button>
              }
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Leaderboard</div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
          {board.length === 0 && <div className="text-white/40 text-xs py-4 px-3 text-center">No approved submissions yet.</div>}
          {board.map((u, i) => (
            <div key={u.user_id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`lb-${u.user_id}`}>
              <div className={`w-6 text-center font-bold ${i === 0 ? "text-amber-300" : i === 1 ? "text-white/80" : i === 2 ? "text-amber-500" : "text-white/40"}`}>{i + 1}</div>
              <div className="flex-1 truncate">{u.user_name}</div>
              <div className="text-[#FF0066] font-bold">{u.total_points}</div>
              <Award className="w-3 h-3 text-amber-300" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ ADMIN CHALLENGES MANAGER ============
export function AdminChallengesButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [subs, setSubs] = useState([]);
  const empty = { title: "", description: "", category: "daily", points: 10, days: 1 };
  const [f, setF] = useState(empty);

  const load = () => {
    api.get("/admin/challenges").then(({ data }) => setList(data)).catch(() => {});
    api.get("/admin/challenges/submissions").then(({ data }) => setSubs(data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const active_until = new Date(Date.now() + f.days * 86400 * 1000).toISOString();
      await api.post("/admin/challenges", { ...f, active_until });
      toast.success("Created"); setF(empty); load(); onDone?.();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  const del = async (cid) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/challenges/${cid}`).catch(() => {}); load(); };
  const approve = async (sid) => { await api.post(`/admin/challenges/submissions/${sid}/approve`).catch(() => toast.error("Failed")); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-admin-challenges"><Flame className="w-4 h-4" /> Challenges</button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Flame className="w-5 h-5 text-amber-300" /> Daily Challenges</DialogTitle>
        </DialogHeader>
        <form onSubmit={create} className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <div className="text-[11px] uppercase text-amber-300 font-bold">➕ New challenge</div>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="Title" />
          <textarea rows={2} required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} placeholder="Description" />
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} placeholder="Category" />
            <input type="number" min="1" value={f.points} onChange={(e) => setF({ ...f, points: Number(e.target.value) })} className={inp} placeholder="Points" />
            <input type="number" min="1" value={f.days} onChange={(e) => setF({ ...f, days: Number(e.target.value) })} className={inp} placeholder="Active days" />
          </div>
          <button className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-4 py-1.5 text-sm" data-testid="create-challenge-btn">Create</button>
        </form>
        <div className="mt-4 grid lg:grid-cols-2 gap-3">
          <div>
            <div className="text-xs uppercase text-fuchsia-300 font-bold mb-2">All challenges ({list.length})</div>
            {list.map((c) => (
              <div key={c.id} className="text-xs rounded border border-white/10 p-2 mb-1.5 flex items-center gap-2">
                <div className="flex-1 truncate"><b>{c.title}</b> <span className="text-white/40">+{c.points}p</span></div>
                <button onClick={() => del(c.id)} className="text-red-300 hover:bg-red-500/10 rounded p-1">×</button>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase text-fuchsia-300 font-bold mb-2">Pending approvals</div>
            {subs.filter((s) => !s.approved).slice(0, 20).map((s) => (
              <div key={s.id} className="text-xs rounded border border-white/10 p-2 mb-1.5">
                <div className="font-medium">{s.user_name}</div>
                <div className="text-white/50 line-clamp-2">{s.submission_text}</div>
                <button onClick={() => approve(s.id)} className="mt-1 bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded text-[10px] font-bold" data-testid={`approve-sub-${s.id}`}>Approve</button>
              </div>
            ))}
            {subs.filter((s) => !s.approved).length === 0 && <div className="text-white/40 text-[11px]">All caught up ✓</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ QR ATTENDANCE ============
export function QRAttendanceButton({ classId, classTitle }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/attendance/qr-token/${classId}`); setData(data); }
    catch (er) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) gen(); /* eslint-disable-next-line */ }, [open, classId]);

  // Use external QR image generator (no library needed)
  const qrImg = data ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(data.qr_payload)}&color=FF0066&bgcolor=050505` : "";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !py-1.5 text-xs" title="QR attendance" data-testid={`qr-${classId}`}><QrCode className="w-3.5 h-3.5" /> QR</button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-md text-center">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 justify-center"><QrCode className="w-5 h-5 text-fuchsia-300" /> Attendance QR</DialogTitle>
          <div className="text-xs text-white/50">{classTitle}</div>
        </DialogHeader>
        {busy && <div className="py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-300" /></div>}
        {data && (
          <div className="space-y-3">
            <img src={qrImg} alt="QR" className="rounded-lg bg-[#050505] p-3 mx-auto" data-testid="qr-image" />
            <div className="text-[11px] text-white/60">Students scan kar ke instantly attendance mark karenge.</div>
            <div className="text-[10px] text-white/40 break-all bg-black/30 rounded px-2 py-1">{data.qr_payload}</div>
            <div className="text-[10px] text-amber-300">⏱ Token expires {new Date(data.expires_at).toLocaleTimeString()}</div>
            <button onClick={gen} className="btn-ghost text-xs w-full justify-center">Regenerate</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ STUDENT QR SCAN ============
export function ScanQRTab() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const mark = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/attendance/mark-qr", { qr_payload: code }); setResult(data); toast.success(`Attendance ${data.status === "marked" ? "marked" : "already marked"} for ${data.class_title}`); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="scan-qr-tab">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-2 flex items-center gap-1"><ScanLine className="w-3.5 h-3.5" /> Mark offline attendance</div>
        <div className="text-xs text-white/60 mb-3">Teacher ke screen pe QR code dikhega — usko scan karke ya paste karke attendance mark karein.</div>
        <form onSubmit={mark} className="space-y-2">
          <input required value={code} onChange={(e) => setCode(e.target.value)} className={inp} placeholder="Paste QR text: cwk-attend:..." data-testid="qr-paste-input" />
          <button className="w-full bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full py-2 text-sm" data-testid="qr-submit">Mark attendance</button>
        </form>
        {result && (
          <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-3 text-xs">
            ✅ <b>{result.status === "marked" ? "Attendance marked!" : "Already marked"}</b> — {result.class_title}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ ADMIN — Auto Batch Suggestions ============
export function AutoBatchSuggestions() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/batches/auto-fill-check").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (course_id) => {
    try { await api.post(`/admin/courses/${course_id}/auto-create-batch`, {}); toast.success("New batch auto-created"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-500/[0.06] p-4 mb-4" data-testid="auto-batch-suggestions">
      <div className="text-xs uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1.5">⚡ Smart batch suggestions</div>
      <div className="text-[11px] text-amber-100/70 mb-2">These batches are 80%+ full — create next batch to avoid losing students.</div>
      <div className="space-y-1.5">
        {items.map((s) => (
          <div key={s.course_id} className="text-xs flex items-center gap-2 bg-black/20 rounded px-2.5 py-1.5">
            <span className="font-medium">{s.course_title}</span>
            <span className="text-amber-200">— {s.fill_percent}% full ({s.enrolled}/{s.capacity})</span>
            <button onClick={() => create(s.course_id)} className="ml-auto bg-[#FF0066] hover:bg-[#e6005c] text-white text-[10px] font-bold rounded-full px-2.5 py-0.5" data-testid={`auto-create-${s.course_id}`}>Auto-create next batch</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ DROP-OFF + FUNNEL + GEO ============
export function GranularAnalyticsPanel() {
  const [funnel, setFunnel] = useState(null);
  const [geo, setGeo] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics/funnel").then(({ data }) => setFunnel(data)).catch(() => {});
    api.get("/admin/analytics/geo").then(({ data }) => setGeo(data)).catch(() => {});
  }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="granular-analytics">
      <div className="glass rounded-xl p-4">
        <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-3">📈 Conversion funnel</div>
        {funnel?.stages.map((s, i) => {
          const max = Math.max(...funnel.stages.map((x) => x.count));
          const pct = max ? (s.count / max) * 100 : 0;
          return (
            <div key={s.label} className="mb-1.5">
              <div className="text-xs flex justify-between"><span>{s.label}</span><span className="font-bold">{s.count}</span></div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#FF0066] to-fuchsia-400" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
      <div className="glass rounded-xl p-4">
        <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-3">🌍 Students by city</div>
        {geo.length === 0 && <div className="text-white/40 text-xs">No city data yet (students haven't set their city in profile).</div>}
        {geo.slice(0, 10).map((g) => (
          <div key={g.city} className="text-xs flex justify-between py-0.5">
            <span>{g.city}</span><span className="text-fuchsia-300 font-bold">{g.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
